<script setup lang="ts">
import { usePropertyValuationStore } from '@/stores/propertyValuation';
import { HgButton } from '@smg-real-estate/domus';
import { storeToRefs } from 'pinia';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';

interface Props {
  nextDisabled?: boolean;
  nextButtonLabel?: string;
  nextButtonLoading?: boolean;
  isSubmit?: boolean;
  onNext?: () => void;
}

withDefaults(defineProps<Props>(), {
  nextDisabled: false,
  nextButtonLabel: '',
  nextButtonLoading: false,
  isSubmit: false,
  onNext: () => {},
});

const { t } = useI18n();
const propertyValuationStore = usePropertyValuationStore();
const { isIv24FunnelActive } = storeToRefs(propertyValuationStore);

// Template ref for the button
const continueButton = ref<InstanceType<typeof HgButton>>();
</script>

<template>
  <div :class="[$style.wrapper, isIv24FunnelActive ? $style.iv24Wrapper : '']">
    <slot>
      <div :class="[$style.buttonWrapper]">
        <HgButton
          ref="continueButton"
          :class="$style.nextButton"
          :type="isSubmit ? 'submit' : 'button'"
          :disabled="nextDisabled"
          :icon="nextButtonLoading ? 'SpinnerHalf' : undefined"
          @click="onNext"
        >
          {{ nextButtonLabel || t('button.continue') }}
        </HgButton>
      </div>
    </slot>
  </div>
</template>

<style lang="scss" module>
@use '@/assets/variables' as *;

.wrapper:not(.iv24Wrapper) {
  position: fixed;
  bottom: 0;
  left: 0;
  z-index: 2;
  width: 100%;
  margin-top: $space-250;
  background-color: $color-white;

  @media screen and (min-width: $funnel-container-max-width) {
    position: static;
  }

  .buttonWrapper {
    display: flex;
    flex-direction: row;
    gap: $space-100;
    justify-content: flex-end;
    padding: $space-100;
    box-shadow: $elevation-medium;

    @media screen and (min-width: $funnel-container-max-width) {
      padding-right: 0;
      padding-left: 0;
      padding-top: 0;
      box-shadow: none;
    }

    @supports (-webkit-hyphens: none) {
      > button > span {
        &::after {
          display: inline-block;
          width: 0;
          content: '\0000a0';
        }
      }
    }
  }
}

.buttonWrapper {
  display: flex;
  justify-content: flex-end;
}

.nextButton {
  width: 100%;
  @include grid-media-query(sm) {
    width: auto;
  }
}
</style>
